/**
 * Resolving the role(s) a user actually holds, for display.
 *
 * Roles are granted per trial in IAM, so "the user's role" is only a
 * well-defined thing inside a trial -- which is why they are shown per trial
 * rather than summarised into one headline role.
 *
 * `/auth/userinfo/` returns both `roles` (one slug per trial, historic) and
 * `all_roles` (every slug per trial). Users routinely hold more than one -- e.g.
 * full_access plus plan_editor -- so `all_roles` is preferred and `roles` is only
 * a fallback for a frontend running against a backend that predates it.
 */

export type RoleMap = Record<string, string | null>;
export type RoleListMap = Record<string, string[]>;

export type RoleBearingUser = {
  roles?: RoleMap;
  all_roles?: RoleListMap;
};

/**
 * Words a slug spells out that are not words: `rd_executive` title-cases to
 * "Rd Executive", which is wrong rather than merely plain.
 *
 * This is a spelling rule, not a catalogue of roles -- it says nothing about
 * which roles exist and needs no edit when one is added or removed. Every entry
 * is here because a real role uses it: `rd` from Acadia's role set, `it` from
 * ClientRole's own `it_administrator` example, `cro` from rolesConfig.
 */
const ACRONYMS: Record<string, string> = {
  rd: "R&D",
  it: "IT",
  cro: "CRO",
};

/** Lower-cased mid-label, as title case normally does. */
const MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "for",
  "in",
  "of",
  "or",
  "the",
  "to",
  "with",
]);

/**
 * Human label for a role slug: `medical_monitor` -> "Medical Monitor",
 * `rd_operations_and_business_optimization` -> "R&D Operations and Business
 * Optimization".
 *
 * Derived rather than looked up, so it cannot drift from the grant the way a
 * hand-maintained role list does. The trade is that a role whose IAM name is
 * not its slug will render a label that quietly disagrees with IAM; carrying
 * `Role.name` through from IAM is the fix, and is not plumbed yet.
 */
export const formatRoleLabel = (slug: string): string =>
  slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      if (index > 0 && MINOR_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");

/** Every role slug the user holds on one trial. */
export const getRoleSlugsForTrial = (
  userInfo: RoleBearingUser | undefined,
  trialId: string | undefined,
): string[] => {
  if (!userInfo || !trialId) return [];

  const all = userInfo.all_roles?.[trialId];
  if (all && all.length > 0) return [...all];

  const single = userInfo.roles?.[trialId];
  return single ? [single] : [];
};
